import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  FileText, Download, Calendar, FileSpreadsheet, 
  File, Loader2, CheckCircle, AlertCircle,
  Pill, Stethoscope, ClipboardList, Heart, Users
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ExportReportOverlay = ({ open, onOpenChange, patient, chartId }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('quick');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedSections, setSelectedSections] = useState({
    demographics: true,
    medications: true,
    orders: true,
    vitals: true,
    care_plan: true,
    assessments: true,
    clinical_notes: false,
    idg_notes: false
  });

  const reportTypes = [
    { id: 'summary', name: 'Chart Summary', description: 'Complete patient overview', icon: FileText },
    { id: 'medications', name: 'Medication List', description: 'Current and past medications', icon: Pill },
    { id: 'orders', name: 'Orders & Procedures', description: 'Physician orders history', icon: ClipboardList },
    { id: 'care_plan', name: 'Care Plan', description: 'Goals, interventions, and POC', icon: Heart },
    { id: 'assessments', name: 'HOPE Assessments', description: 'Assessment history', icon: Stethoscope },
    { id: 'idg', name: 'IDG Summary', description: 'Interdisciplinary notes', icon: Users }
  ];

  const datePresets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 6 months', days: 180 },
    { label: 'Last year', days: 365 },
    { label: 'All time', days: 0 }
  ];

  const handleDatePreset = (days) => {
    if (days === 0) {
      setDateRange({
        start: '2020-01-01',
        end: new Date().toISOString().split('T')[0]
      });
    } else {
      setDateRange({
        start: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      });
    }
  };

  const handleQuickExport = async (reportType) => {
    if (!chartId) {
      toast.error("Please select a patient first");
      return;
    }

    setLoading(true);
    try {
      const endpoint = `/api/ehr/export/charts/${chartId}/${reportType}/pdf`;
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_${patient?.name || 'patient'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report exported successfully`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomExport = async () => {
    if (!chartId) {
      toast.error("Please select a patient first");
      return;
    }

    const sectionsToExport = Object.entries(selectedSections)
      .filter(([_, selected]) => selected)
      .map(([section, _]) => section);

    if (sectionsToExport.length === 0) {
      toast.error("Please select at least one section to export");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        format: exportFormat,
        start_date: dateRange.start,
        end_date: dateRange.end,
        sections: sectionsToExport.join(',')
      });

      const endpoint = `/api/ehr/export/charts/${chartId}/custom?${params}`;
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        // Try fallback to summary endpoint
        const summaryResponse = await fetch(`${API_URL}/api/ehr/export/charts/${chartId}/summary/pdf`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!summaryResponse.ok) {
          throw new Error(`Export failed: ${response.status}`);
        }
        
        const blob = await summaryResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `patient_report_${patient?.name || 'patient'}_${dateRange.start}_${dateRange.end}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("Chart summary exported successfully");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = exportFormat === 'excel' ? 'xlsx' : exportFormat;
      a.download = `patient_report_${patient?.name || 'patient'}_${dateRange.start}_${dateRange.end}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Custom report exported successfully as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 border dark:border-slate-700" data-testid="export-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Download className="h-5 w-5 text-teal-600" />
            Export Patient Report
          </DialogTitle>
          <DialogDescription asChild>
            <div>
              {patient ? (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  Exporting for <Badge variant="outline">{patient.name}</Badge>
                </span>
              ) : (
                <span className="text-amber-600 text-sm">Please select a patient to export their report</span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="quick">Quick Export</TabsTrigger>
            <TabsTrigger value="custom">Custom Report</TabsTrigger>
          </TabsList>

          {/* Quick Export Tab */}
          <TabsContent value="quick" className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Select a report type for instant PDF download
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {reportTypes.map((report) => {
                const Icon = report.icon;
                return (
                  <Button
                    key={report.id}
                    variant="outline"
                    className="h-auto p-4 flex flex-col items-start gap-2 hover:border-teal-500 hover:bg-teal-50/50"
                    onClick={() => handleQuickExport(report.id)}
                    disabled={loading || !chartId}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Icon className="h-5 w-5 text-teal-600" />
                      <span className="font-medium">{report.name}</span>
                    </div>
                    <span className="text-xs text-slate-500 text-left">{report.description}</span>
                  </Button>
                );
              })}
            </div>
          </TabsContent>

          {/* Custom Report Tab */}
          <TabsContent value="custom" className="space-y-6 mt-4">
            {/* Date Range Section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date Range
              </Label>
              
              <div className="flex flex-wrap gap-2">
                {datePresets.map((preset) => (
                  <Button
                    key={preset.days}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => handleDatePreset(preset.days)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="h-9"
                  />
                </div>
              </div>
            </div>

            {/* Sections Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Include Sections</Label>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'demographics', label: 'Demographics & Contacts' },
                  { id: 'medications', label: 'Medications' },
                  { id: 'orders', label: 'Orders & Procedures' },
                  { id: 'vitals', label: 'Vital Signs' },
                  { id: 'care_plan', label: 'Care Plan & Goals' },
                  { id: 'assessments', label: 'HOPE Assessments' },
                  { id: 'clinical_notes', label: 'Clinical Notes' },
                  { id: 'idg_notes', label: 'IDG Meeting Notes' }
                ].map((section) => (
                  <div
                    key={section.id}
                    className="flex items-center space-x-2 p-2 rounded-md border hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleSection(section.id)}
                  >
                    <Checkbox
                      id={section.id}
                      checked={selectedSections[section.id]}
                      onCheckedChange={() => toggleSection(section.id)}
                    />
                    <Label htmlFor={section.id} className="text-sm cursor-pointer flex-1">
                      {section.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Export Format */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Export Format</Label>
              
              <div className="flex gap-3">
                <Button
                  variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setExportFormat('pdf')}
                  className="flex items-center gap-2"
                >
                  <File className="h-4 w-4" />
                  PDF
                </Button>
                <Button
                  variant={exportFormat === 'excel' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setExportFormat('excel')}
                  className="flex items-center gap-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Excel
                </Button>
              </div>
            </div>

            {/* Export Button */}
            <Button
              className="w-full bg-teal-600 hover:bg-teal-700"
              onClick={handleCustomExport}
              disabled={loading || !chartId}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export Custom Report
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>

        {/* Status Messages */}
        {!chartId && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-4">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-700">
              Select a patient from the dashboard to enable export
            </span>
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mt-4">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-sm text-blue-700">
              Generating your report... This may take a moment.
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExportReportOverlay;
